import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { client, MinimalEntityDto } from "@/client";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useNavigate } from "@tanstack/react-router";
import { useTable } from "@/components/custom/data-table/data-table-context";
import { Input } from "@/components/ui/input";
import DialogComponent from "@/components/custom/dialog";
import SelectInput, {
  OptionType,
} from "@/components/select-input/select-input";
import { GroupBase } from "react-select";

import { showActionErrorToast } from "@/state/error-toast";

export const AddSite = () => {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  if (permissions.sites.create) {
    return (
      <DialogComponent
        label={t("addSite")}
        title={t("addNewSite")}
        contentClassName={"max-w-[50vh]"}
      >
        <AddSiteForm />
      </DialogComponent>
    );
  }
  return <></>;
};

const formSchema = z.object({
  siteName: z.string().min(1, { message: "Please enter site name" }),
  deviceIds: z.array(z.object({ value: z.number(), label: z.string() })),
});

const AddSiteForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: allDevicesMinimalResults } = useQuery({
    ...client.devices.listMinimal
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
  });
  const { data: unassignedDevicesResults } = useQuery({
    ...client.devices.listMinimal
      .assign({
        page: 1,
        limit: 9999,
        siteId: -1,
      })
      .asQueryOptions({}),
  });

  const { groupedOptions } = useMemo(() => {
    //
    const allDevicesMap = new Map(
      (allDevicesMinimalResults?.results ?? []).map(
        (device: MinimalEntityDto) => [device.id, device],
      ),
    );
    (unassignedDevicesResults?.results ?? []).forEach(
      (device: MinimalEntityDto) => allDevicesMap.delete(device.id),
    );
    const devicesRest: OptionType[] = [...allDevicesMap.values()].map(
      (value) => ({ label: value.name, value: value.id }),
    );
    //
    const unassignedDevices: OptionType[] = (
      unassignedDevicesResults?.results ?? []
    )
      .map((result) => {
        if (
          result !== null &&
          typeof result === "object" &&
          "id" in result &&
          "name" in result
        ) {
          return {
            label: result.name as any as string,
            value: result.id as any as number,
          };
        } else {
          return null;
        }
      })
      .filter((element) => {
        return element !== null;
      })
      .sort((a, b) => a.value - b.value);
    //
    const groupedOptions: readonly GroupBase<{
      value: number;
      label: string;
    }>[] = [
      {
        label: t("unassignedDevices"),
        options: unassignedDevices as readonly {
          value: number;
          label: string;
        }[],
      },
      {
        label: t("assignedDevices"),
        options: devicesRest as readonly { value: number; label: string }[],
      },
    ];

    return {
      groupedOptions,
    };
  }, [allDevicesMinimalResults, unassignedDevicesResults]);

  const createSite = useMutation({
    ...client.sites.create.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });
  const table = useTable();
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      siteName: "",
      deviceIds: [],
    },
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const deviceIds = data.deviceIds.map((device) => device.value);
    const site = await createSite.mutateAsync({
      requestBody: { name: data.siteName, deviceIds },
    });
    table?.setPreview(2);
    table?.setPosition({});
    queryClient.invalidateQueries({
      queryKey: client.sites.listSummaries.queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: client.sites.listMinimal.queryKey,
    });
    navigate({
      to: "/dashboard/resources/sites/$siteId",
      params: { siteId: site.id as any },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="pb-4 flex flex-col gap-4">
          <FormField
            control={form.control}
            name="siteName"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>{t("siteName")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("addSite")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="deviceIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("associateDevices")}</FormLabel>
                <FormControl>
                  <SelectInput
                    placeholder={t("selectDevices")}
                    {...field}
                    classNames={{
                      control: () => "min-h-16 max-h-28 overflow-y-scroll",
                    }}
                    isMulti={true}
                    isClearable={true}
                    options={groupedOptions}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">{t("cancel")}</Button>
          </DialogClose>
          <Button
            variant="azure"
            disabled={!form.formState.isDirty || !form.formState.isValid}
          >
            {t("done")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
